// src/utils/enhanceIdea.js
// Utility functions for AI-powered idea enhancement

import { auth } from '../firebase';
import { getApiEndpoint } from './apiBaseUrl';

/** Stable API denial code (US-02/US-04) — also on thrown Error as `error.code` */
export const ENTITLEMENT_REQUIRED = 'ENTITLEMENT_REQUIRED';

/** US-11: complimentary AI pass requires verified email */
export const EMAIL_VERIFICATION_REQUIRED = 'EMAIL_VERIFICATION_REQUIRED';

/** US-11: verified email already used complimentary pass (another account) */
export const FREE_PASS_IDENTITY_EXHAUSTED = 'FREE_PASS_IDENTITY_EXHAUSTED';

/** Refresh Firebase ID token after these 403 codes (stale claims after verify / entitlement change) */
export const ENHANCEMENT_DENIAL_REFRESH_CODES = new Set([
  ENTITLEMENT_REQUIRED,
  EMAIL_VERIFICATION_REQUIRED,
]);

/**
 * @param {string} message
 * @param {string} [code]
 * @returns {Error}
 */
function createApiError(message, code) {
  const err = new Error(message);
  if (code) err.code = code;
  return err;
}

/**
 * Enhance idea using AI
 * @param {string} description - Required description of the idea
 * @param {string} existingTitle - Optional existing title (if provided, AI may refine it)
 * @returns {Promise<Object>} Enhanced idea data with title, tags, subtasks, planSteps
 */
export const enhanceIdea = async (description, existingTitle = '') => {
  if (!description || typeof description !== 'string' || description.trim().length === 0) {
    throw new Error('Description is required for enhancement');
  }
  
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User must be authenticated to enhance ideas');
  }
  
  // Get ID token for authentication
  let idToken;
  try {
    idToken = await user.getIdToken(true); // Force refresh
  } catch (error) {
    console.error('[enhanceIdea] Error getting ID token:', error);
    throw new Error('Authentication failed. Please sign in again.');
  }
  
  const apiUrl = getApiEndpoint('/enhance-idea');
  
  try {
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`
      },
      body: JSON.stringify({ 
        description: description.trim(),
        existingTitle: existingTitle ? existingTitle.trim() : ''
      })
    });
    
    // Check if response is JSON before parsing
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      const text = await response.text();
      console.error('[enhanceIdea] Non-JSON response:', {
        status: response.status,
        statusText: response.statusText,
        contentType,
        preview: text.substring(0, 200)
      });
      
      if (response.status === 404) {
        throw new Error('API endpoint not found. Please check that the server is running and the endpoint is configured.');
      } else if (response.status >= 500) {
        throw new Error('Server error. Please try again later or contact support.');
      } else {
        throw new Error(`Unexpected response from server (${response.status}). Please try again.`);
      }
    }
    
    const data = await response.json();
    
    if (!response.ok) {
      // Handle specific error codes
      if (response.status === 401) {
        throw new Error('Authentication failed. Please sign in again.');
      } else if (response.status === 403) {
        throw createApiError(
          data.error ||
            "AI enhancement isn't available for your account right now. Try refreshing the app or signing out and back in. Contact Support if you need help.",
          data.code
        );
      } else if (response.status === 400) {
        throw new Error(data.error || 'Invalid request. Please check your input.');
      } else if (response.status === 503) {
        if (data.code === 'FEATURE_DISABLED') {
          throw new Error('Enhancement feature is currently disabled. Please contact admin.');
        } else if (data.code === 'OPENAI_NOT_CONFIGURED') {
          throw new Error('AI service is not configured. Please contact support.');
        }
        throw new Error(data.error || 'Service unavailable. Please try again later.');
      } else if (response.status === 429) {
        if (data.code === 'QUOTA_EXCEEDED') {
          const errorMsg = data.details 
            ? `${data.error}\n\n${data.details}`
            : data.error || 'OpenAI API quota exceeded. Please check your account billing.';
          throw new Error(errorMsg);
        } else {
          throw new Error('Rate limit exceeded. Please try again in a moment.');
        }
      } else if (response.status === 500) {
        throw new Error(data.error || 'Failed to enhance idea. Please try again.');
      } else {
        throw new Error(data.error || 'Failed to enhance idea');
      }
    }
    
    return data;
  } catch (error) {
    // Handle JSON parsing errors specifically
    if (error instanceof SyntaxError && error.message.includes('JSON')) {
      throw new Error('Server returned invalid response. Please check that the API endpoint is configured correctly.');
    }
    if (error instanceof TypeError && error.message.includes('fetch')) {
      throw new Error('Network error. Please check your connection and try again.');
    }
    throw error;
  }
};

/**
 * Enhance idea using AI - step-by-step for Guided Journey
 * @param {string} description - Original idea description
 * @param {number} step - Step number (1, 2, or 3)
 * @param {object} context - Context from previous steps (for steps 2 and 3)
 * @param {string} feedback - Optional user feedback for regeneration
 * @param {string} existingTitle - Optional existing title
 * @returns {Promise<Object>} Step-specific enhanced data
 *   - Step 1: { title, description }
 *   - Step 2: { tags, subtasks }
 *   - Step 3: { planSteps }
 */
export const enhanceIdeaStep = async (description, step, context = {}, feedback = '', existingTitle = '') => {
  if (!description || typeof description !== 'string' || description.trim().length === 0) {
    throw new Error('Description is required for enhancement');
  }
  
  if (![1, 2, 3].includes(step)) {
    throw new Error('Step must be 1, 2, or 3');
  }
  
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User must be authenticated to enhance ideas');
  }
  
  // Get ID token for authentication
  let idToken;
  try {
    idToken = await user.getIdToken(true); // Force refresh
  } catch (error) {
    console.error('[enhanceIdeaStep] Error getting ID token:', error);
    throw new Error('Authentication failed. Please sign in again.');
  }
  
  const apiUrl = getApiEndpoint('/enhance-idea');
  
  try {
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`
      },
      body: JSON.stringify({ 
        description: description.trim(),
        existingTitle: existingTitle ? existingTitle.trim() : '',
        step,
        context,
        feedback: feedback ? feedback.trim() : ''
      })
    });
    
    // Check if response is JSON before parsing
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      const text = await response.text();
      console.error('[enhanceIdeaStep] Non-JSON response:', {
        status: response.status,
        statusText: response.statusText,
        contentType,
        preview: text.substring(0, 200)
      });
      
      if (response.status === 404) {
        throw new Error('API endpoint not found. Please check that the server is running and the endpoint is configured.');
      } else if (response.status >= 500) {
        throw new Error('Server error. Please try again later or contact support.');
      } else {
        throw new Error(`Unexpected response from server (${response.status}). Please try again.`);
      }
    }
    
    const data = await response.json();
    
    if (!response.ok) {
      // Handle specific error codes
      if (response.status === 401) {
        throw new Error('Authentication failed. Please sign in again.');
      } else if (response.status === 403) {
        throw createApiError(
          data.error ||
            "AI enhancement isn't available for your account right now. Try refreshing the app or signing out and back in. Contact Support if you need help.",
          data.code
        );
      } else if (response.status === 400) {
        throw new Error(data.error || 'Invalid request. Please check your input.');
      } else if (response.status === 503) {
        if (data.code === 'FEATURE_DISABLED') {
          throw new Error('Enhancement feature is currently disabled. Please contact admin.');
        } else if (data.code === 'OPENAI_NOT_CONFIGURED') {
          throw new Error('AI service is not configured. Please contact support.');
        }
        throw new Error(data.error || 'Service unavailable. Please try again later.');
      } else if (response.status === 429) {
        if (data.code === 'QUOTA_EXCEEDED') {
          const errorMsg = data.details 
            ? `${data.error}\n\n${data.details}`
            : data.error || 'OpenAI API quota exceeded. Please check your account billing.';
          throw new Error(errorMsg);
        } else {
          throw new Error('Rate limit exceeded. Please try again in a moment.');
        }
      } else if (response.status === 409) {
        throw new Error(
          data.error || 'Could not apply free pass for this step. Refresh and try again, or contact support.'
        );
      } else if (response.status === 500) {
        throw new Error(data.error || 'Failed to enhance idea. Please try again.');
      } else {
        throw new Error(data.error || 'Failed to enhance idea');
      }
    }
    
    return data;
  } catch (error) {
    // Handle JSON parsing errors specifically
    if (error instanceof SyntaxError && error.message.includes('JSON')) {
      throw new Error('Server returned invalid response. Please check that the API endpoint is configured correctly.');
    }
    if (error instanceof TypeError && error.message.includes('fetch')) {
      throw new Error('Network error. Please check your connection and try again.');
    }
    throw error;
  }
};

/**
 * US-03: Record complimentary Autopilot free-pass consumption after client persists the idea.
 * @param {string} idempotencyKey - UUID or unique string (≥8 chars per server validation)
 * @returns {Promise<object>} Server JSON (ok, code, freePassCreditsRemaining)
 */
export const consumeFreePassAutopilot = async (idempotencyKey) => {
  const trimmed = String(idempotencyKey || '').trim();
  if (trimmed.length < 8) {
    throw new Error('idempotencyKey must be at least 8 characters.');
  }
  if (trimmed.length > 128) {
    throw new Error('idempotencyKey must be at most 128 characters.');
  }

  const user = auth.currentUser;
  if (!user) {
    throw new Error('User must be authenticated to consume free pass');
  }

  let idToken;
  try {
    idToken = await user.getIdToken(true);
  } catch (error) {
    console.error('[consumeFreePassAutopilot] Error getting ID token:', error);
    throw new Error('Authentication failed. Please sign in again.');
  }

  const apiUrl = getApiEndpoint('/consume-free-pass');

  const response = await fetch(apiUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({
      channel: 'autopilot',
      idempotencyKey: trimmed,
    }),
  });

  const contentType = response.headers.get('content-type');
  if (!contentType || !contentType.includes('application/json')) {
    const text = await response.text();
    console.error('[consumeFreePassAutopilot] Non-JSON response:', {
      status: response.status,
      preview: text.substring(0, 200),
    });
    throw new Error('Unexpected response from server.');
  }

  const data = await response.json();

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Authentication failed. Please sign in again.');
    }
    throw createApiError(data.error || 'Free pass consume failed.', data.code);
  }

  return data;
};
